import { Edge, Node } from '@xyflow/react';

import uuidv4 from '@/lib/uuid';

import { isGptImage2FamilyModel } from '@/constants/gptImageModels';
import { getNanoBananaCredits } from '@/constants/nanoBananaCredits';
import {
  getReferenceImageConfigForModel,
  getVideoModelCredits as getModelCredits,
  ReferenceImageType,
  ResolutionType,
} from '@/constants/videoModels';

import { useWorkflowStore } from './store';
import { getExecutionOrder, resolveVariables } from './validator';

async function extractFrameFromVideo(
  videoUrl: string,
  preferLast: boolean,
): Promise<string | null> {
  try {
    const framesRes = await fetch('/api/extract-video-frames', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        videoUrl,
        frames: [preferLast ? 'last' : 'first'],
      }),
    });
    if (!framesRes.ok) return null;
    const frames = await framesRes.json();
    return preferLast
      ? frames.lastFrame || frames.firstFrame
      : frames.firstFrame || frames.lastFrame;
  } catch {
    return null;
  }
}

async function getImageUrlFromSourceResult(
  sourceResult: any,
  edge: Edge,
): Promise<string | null> {
  if (!sourceResult) return null;
  // If we got an array of image URLs, prefer the first one by default
  if (Array.isArray(sourceResult) && sourceResult.length > 0) {
    const first = sourceResult[0];
    return typeof first === 'string' ? first : null;
  }
  if (typeof sourceResult === 'string') {
    const url = sourceResult;
    const looksLikeVideo =
      typeof url === 'string' &&
      (url.includes('/videos.') || /\.(mp4|webm|mov)(\?|$)/i.test(url));
    if (looksLikeVideo) {
      const preferLast =
        (edge.sourceHandle as string | undefined) === 'last-frame';
      return await extractFrameFromVideo(url, preferLast);
    }
    return url;
  }

  if (typeof sourceResult === 'object') {
    const handle = edge.sourceHandle as string | undefined;
    const preferLast = handle === 'last-frame';
    const first = (sourceResult as any).firstFrame as string | undefined;
    const last = (sourceResult as any).lastFrame as string | undefined;

    if (preferLast && last) return last;
    if (!preferLast && first) return first;
    if (last || first) return last || first || null;

    const videoUrl =
      (sourceResult as any).videoUrl ||
      (sourceResult as any).video_url ||
      (sourceResult as any).url;
    if (typeof videoUrl === 'string') {
      return await extractFrameFromVideo(videoUrl, preferLast);
    }
  }
  return null;
}

export interface ExecutionContext {
  variables: Record<string, any>;
  nodeResults: Record<string, any>;
  totalCreditsUsed: number;
}

export class WorkflowExecutionError extends Error {
  constructor(
    message: string,
    public nodeId: string,
    public nodeName?: string,
  ) {
    super(message);
    this.name = 'WorkflowExecutionError';
  }
}

/**
 * Calculate estimated credits for a workflow
 */
export function calculateWorkflowCredits(nodes: Node[]): number {
  let totalCredits = 0;

  nodes.forEach((node) => {
    const credits = getNodeCreditCost(node);
    totalCredits += credits;
  });

  return totalCredits;
}

/**
 * Get credit cost for a specific node
 */
export function getNodeCreditCost(node: Node): number {
  const config: Record<string, unknown> = (node.data?.config ?? {}) as Record<
    string,
    unknown
  >;

  // Helpers to safely read typed values from the loosely-typed config
  const getString = (key: string, fallback: string): string => {
    const value = config[key];
    return typeof value === 'string' ? value : fallback;
  };
  const getNumber = (key: string, fallback: number): number => {
    const value = config[key];
    return typeof value === 'number' ? value : fallback;
  };
  const getBoolean = (key: string, fallback: boolean): boolean => {
    const value = config[key];
    return typeof value === 'boolean' ? value : fallback;
  };

  switch (node.type) {
    case 'image-generation': {
      const model = getString('model', 'Nano Banana Pro');
      const outputs = getNumber('outputs', 1);
      const dimensions = getString('dimensions', '1:1');
      return getImageModelCredits(model, dimensions) * outputs;
    }

    case 'image-edit': {
      const editModel = getString('editModel', 'Nano Banana Pro');
      const useHD = getBoolean('useHD', false);
      const resolution = getString('resolution', '2K');

      // Base credits per edit model
      const editModelCredits: Record<string, number> = {
        // Nano Banana family — see @/constants/nanoBananaCredits.
        'Nano Banana 2 Lite': getNanoBananaCredits('Nano Banana 2 Lite')!,
        'Nano Banana 2': getNanoBananaCredits('Nano Banana 2', resolution)!,
        'Nano Banana Pro': getNanoBananaCredits('Nano Banana Pro', resolution)!,
        'Nano Banana Flash': getNanoBananaCredits(
          'Nano Banana Flash',
          resolution,
        )!,
        'FLUX.2 [pro]': 2,
        'FLUX.2 [flex]': resolution === '2K' ? 6 : 3,
        'Seedream 5.0 Lite': 2,
        'Seedream 5.0 Pro': resolution === '1K' ? 4 : 8,
        'Seedream 4.5': 2,
        'Seedream 4.0': 2,
        'Nano Banana': getNanoBananaCredits('Nano Banana')!,
        'Qwen Image': 2,
        Reve: 2,
        'Reve Fast': 0.5,
        'Flux Kontext Pro': 2,
        'Flux Kontext Max': 4,
        'OpenAI GPT Image 1': 8,
        'OpenAI GPT Image 1.5': useHD ? 8 : 2,
        'OpenAI GPT Image 2 Low': 0.5,
        'OpenAI GPT Image 2 Medium': 3,
        'OpenAI GPT Image 2 High': 11,
        'OpenAI GPT Image 2.5 Flare Low': 0.5,
        'OpenAI GPT Image 2.5 Flare Medium': 3,
        'OpenAI GPT Image 2.5 Flare High': 11,
        'OpenAI GPT Image 2.5 Sunburst Medium': 3,
        'OpenAI GPT Image 2.5 Sunburst High': 11,
        'Ideogram v3 Character': 5,
      };

      const baseCredits = editModelCredits[editModel] || 2;
      // For GPT Image 1.5 and the GPT Image 2/2.5 family, credits are already per-model; don't apply useHD multiplier
      if (
        editModel === 'OpenAI GPT Image 1.5' ||
        isGptImage2FamilyModel(editModel)
      ) {
        return baseCredits;
      }
      return useHD ? baseCredits * 1.5 : baseCredits;
    }

    case 'image-upscaler': {
      const scaleFactor = getNumber('scaleFactor', 2);
      return 1 * scaleFactor;
    }

    case 'text-to-video':
    case 'image-to-video':
    case 'first-last-frame-video':
    case 'references-to-video': {
      const model = getString('model', 'Veo 3.1 Fast Image');
      const duration = getString('duration', '5');
      const resolution = getString('resolution', '') as
        | ResolutionType
        | undefined;
      const generateAudio = config['generateAudio'] as boolean | undefined;
      return getModelCredits(
        model,
        duration,
        resolution || undefined,
        generateAudio,
      );
    }

    case 'text-to-speech':
    case 'music-generation': {
      return 0; // TTS/music uses character count, not credits
    }

    case 'talking-video': {
      // Cost depends on final audio duration, which is not known until runtime.
      // Returning 0 here mirrors how text-to-speech and music-generation are treated;
      // the node UI shows an estimate based on text length / connected audio.
      return 0;
    }

    case 'text-generation': {
      return 0; // Text generation uses word count, not credits
    }

    case 'text-preset': {
      return 0; // Text preset uses word count, not credits
    }

    default:
      return 0; // Input, output, note nodes are free
  }
}

/**
 * Get credit cost for image models
 * Based on src/constants/imageModels.ts pricing
 * @param model - Model name
 * @param dimensions - Optional dimensions/resolution for models with resolution-dependent pricing
 */
function getImageModelCredits(model: string, dimensions?: string): number {
  const is4K = dimensions?.includes('4K') || dimensions?.includes('4k');
  const is2K = dimensions?.includes('2K') || dimensions?.includes('2k');

  // Nano Banana family — see @/constants/nanoBananaCredits.
  const nanoBananaCredits = getNanoBananaCredits(model, dimensions);
  if (nanoBananaCredits !== null) {
    return nanoBananaCredits;
  }
  if (model === 'Z-Image Turbo') {
    return is2K ? 1 : 0.1; // 0.1 credits (1K), 1 credit (2K)
  }
  if (model === 'FLUX.2 [max]') {
    return is2K ? 5 : 2.5; // 2.5 credits (1K), 5 credits (2K)
  }
  if (model === 'FLUX.2 [flex]') {
    return is2K ? 6 : 3; // 3 credits (1K), 6 credits (2K)
  }

  const creditMap: Record<string, number> = {
    // Budget models (0.1-0.5 credits)
    'FLUX.1': 0.25,
    'Stable Diffusion XL': 0.25,
    'Kandinsky 2': 0.5,
    'Kandinsky 2.2': 0.5,
    'Kandinsky 3.0': 0.5,
    'MiniMax Image 01': 0.5,

    // Fast/Standard models (1-2 credits)
    'FLUX.2 [dev]': 1,
    'GPT Image 1': 2,
    'OpenAI GPT Image 1': 2,
    'OpenAI GPT Image 1.5': 2,
    'OpenAI GPT Image 2 Low': 0.5,
    'OpenAI GPT Image 2 Medium': 3,
    'OpenAI GPT Image 2 High': 11,
    'OpenAI GPT Image 2.5 Flare Low': 0.5,
    'OpenAI GPT Image 2.5 Flare Medium': 3,
    'OpenAI GPT Image 2.5 Flare High': 11,
    'OpenAI GPT Image 2.5 Sunburst Medium': 3,
    'OpenAI GPT Image 2.5 Sunburst High': 11,
    'Seedream 3.0': 2,
    'Seedream 4.0': 2,
    'Seedream 4.5': 2,
    'Seedream 5.0 Lite': 2,
    'Seedream 5.0 Pro': 8,
    'Dreamina 3.1': 2,
    'Recraft v3': 2,
    'Recraft v4.1': 2,
    'Recraft v4.1 Vector': 4,
    'Recraft v4.1 Pro': 10,
    'Recraft v4.1 Pro Vector': 10,
    'Stable Diffusion 3.5': 2,
    'Stable Diffusion 3.0': 2,
    'WAN 2.2': 2,
    'WAN 2.5 Preview': 2,
    'Bria 3.2': 2,
    HiDream: 2,
    REVE: 2,
    'REVE Fast': 2,
    'Qwen-Image': 2,
    Grok: 2,
    'Grok Imagine 2.0': 3,
    'Muse Image': 1,
    'ImagineArt 1.5': 2,
    'Mai Image 2.5': 2,
    'Qwen Image Max': 4,
    'Qwen Image 2.0': 2,
    'WAN 2.7 Pro': 4,
    'WAN 2.7': 2,
    'Krea v2 Large': 3,
    'Krea v2 Medium': 2,
    'Krea v2 Medium Turbo': 1,
    'Luma Uni v1': 2,
    'Boogu Image': 3,
    'Cosmos 3 Super': 2,
    'P-Image': 0.5,
    'FLUX.2 [pro]': 2,
    'FLUX 1.1 Pro': 2,
    'Flux.1 Krea': 2,
    'Flux Kontext Pro': 2,

    // Mid-tier models (3-5 credits)
    'Ideogram v3': 3,
    'Ideogram v4': 3,
    'Flux Kontext Max': 4,
    'FLUX 1.1 Pro Ultra': 4,
    'HunyuanImage 3.0': 5,
    'Midjourney V6': 5,
    'Midjourney V7': 5,
  };

  return creditMap[model] || 2; // Default to 2 credits
}

/**
 * Execute a workflow
 */
export async function executeWorkflow(
  nodes: Node[],
  edges: Edge[],
  initialInputs: Record<string, any> = {},
): Promise<ExecutionContext> {
  const context: ExecutionContext = {
    variables: { ...initialInputs },
    nodeResults: {},
    totalCreditsUsed: 0,
  };

  // Get execution order (levels for parallel execution)
  const executionLevels = getExecutionOrder(nodes, edges);

  // Get store methods
  const store = useWorkflowStore.getState();

  // Execute nodes level by level
  for (const level of executionLevels) {
    // Execute all nodes in this level in parallel
    const levelPromises = level.map(async (nodeId) => {
      const node = nodes.find((n) => n.id === nodeId);
      if (!node) return;

      try {
        // Update node status to running
        store.setNodeStatus(nodeId, 'running');

        // Execute the node
        const result = await executeNode(node, edges, context);

        // Store result
        context.nodeResults[nodeId] = result;

        // Update variables for next nodes
        context.variables[nodeId] = result;

        // Update node status to success
        store.setNodeStatus(nodeId, 'success');
        store.setNodeResult(nodeId, result);

        // Add credits used
        const creditsUsed = getNodeCreditCost(node);
        context.totalCreditsUsed += creditsUsed;
      } catch (error: any) {
        // Update node status to error
        store.setNodeStatus(nodeId, 'error');
        store.setNodeError(nodeId, error.message);

        // Throw error to stop level execution
        throw new WorkflowExecutionError(
          error.message,
          nodeId,
          String(node.data?.label || node.id),
        );
      }
    });

    // Wait for all nodes in this level to complete
    // If any node fails, Promise.all will reject and stop execution
    await Promise.all(levelPromises);
  }

  return context;
}

/**
 * Execute a single node and its dependencies
 */
export async function executeSingleNode(
  nodeId: string,
  nodes: Node[],
  edges: Edge[],
): Promise<any> {
  const store = useWorkflowStore.getState();

  // Hydrate cached results from node data if the store was reset (e.g. after reload)
  const hydratedResults: Record<string, any> = { ...store.nodeResults };
  const missingResults: Record<string, any> = {};

  nodes.forEach((node) => {
    const nodeResult = (node.data as { result?: unknown })?.result;
    const hasStoredResult =
      hydratedResults[node.id] !== undefined &&
      hydratedResults[node.id] !== null &&
      hydratedResults[node.id] !== '';
    const hasNodeResult =
      nodeResult !== undefined && nodeResult !== null && nodeResult !== '';

    if (!hasStoredResult && hasNodeResult) {
      hydratedResults[node.id] = nodeResult;
      missingResults[node.id] = nodeResult;
    }
  });

  if (Object.keys(missingResults).length > 0) {
    useWorkflowStore.setState((state) => ({
      nodeResults: { ...state.nodeResults, ...missingResults },
    }));
  }

  // Get the target node
  const targetNode = nodes.find((n) => n.id === nodeId);
  if (!targetNode) {
    throw new Error('Node not found');
  }

  // Find all nodes that this node depends on (incoming edges)
  const getDependencies = (
    nId: string,
    visited = new Set<string>(),
  ): string[] => {
    if (visited.has(nId)) return [];
    visited.add(nId);

    const incomingEdges = edges.filter((e) => e.target === nId);
    const deps: string[] = [];

    for (const edge of incomingEdges) {
      deps.push(edge.source);
      deps.push(...getDependencies(edge.source, visited));
    }

    return deps;
  };

  const dependencies = getDependencies(nodeId);
  const uniqueDeps = Array.from(new Set(dependencies));

  // Get execution order for dependencies + target node (levels for parallel execution)
  const nodesToExecute = [...uniqueDeps, nodeId];
  const executionLevels = getExecutionOrder(
    nodes.filter((n) => nodesToExecute.includes(n.id)),
    edges.filter(
      (e) =>
        nodesToExecute.includes(e.source) && nodesToExecute.includes(e.target),
    ),
  );

  // Create execution context with existing results
  const context: ExecutionContext = {
    variables: { ...hydratedResults },
    nodeResults: { ...hydratedResults },
    totalCreditsUsed: 0,
  };

  // Execute dependencies and target node level by level
  for (const level of executionLevels) {
    // Execute all nodes in this level in parallel
    const levelPromises = level.map(async (execNodeId) => {
      const node = nodes.find((n) => n.id === execNodeId);
      if (!node) return;

      // Skip executing dependency nodes that already have results
      // Only re-execute the target node
      const isTargetNode = execNodeId === nodeId;

      // Check for existing result - must be non-null and non-empty
      const hasExistingResult =
        context.nodeResults[execNodeId] !== undefined &&
        context.nodeResults[execNodeId] !== null &&
        context.nodeResults[execNodeId] !== '';

      if (!isTargetNode && hasExistingResult) {
        // Skip execution, use cached result
        console.log(`[Workflow] Skipping ${execNodeId} - using cached result`);
        return;
      }

      // For target node, clear previous error before re-executing
      if (isTargetNode) {
        store.setNodeError(execNodeId, '');
      }

      try {
        // Update node status to running
        store.setNodeStatus(execNodeId, 'running');

        console.log(
          `[Workflow] Executing ${execNodeId}${isTargetNode ? ' (target)' : ' (dependency)'}`,
        );

        // Execute the node
        const result = await executeNode(node, edges, context);

        // Store result
        context.nodeResults[execNodeId] = result;
        context.variables[execNodeId] = result;

        // Update node status to success
        store.setNodeStatus(execNodeId, 'success');
        store.setNodeResult(execNodeId, result);

        // Add credits used
        const creditsUsed = getNodeCreditCost(node);
        context.totalCreditsUsed += creditsUsed;
      } catch (error: any) {
        // Update node status to error
        store.setNodeStatus(execNodeId, 'error');
        store.setNodeError(execNodeId, error.message);

        throw new WorkflowExecutionError(
          error.message,
          execNodeId,
          String(node.data?.label || node.id),
        );
      }
    });

    // Wait for all nodes in this level to complete
    await Promise.all(levelPromises);
  }

  return context.nodeResults[nodeId];
}

/**
 * Execute a single node
 */
async function executeNode(
  node: Node,
  edges: Edge[],
  context: ExecutionContext,
): Promise<any> {
  const config = node.data?.config || {};

  // Get input from connected nodes
  const input = getNodeInput(node.id, edges, context);

  switch (node.type) {
    case 'input':
      return executeInputNode(node, config);

    case 'upload':
      return executeUploadNode(node, config);

    case 'select-image':
      return executeSelectImageNode(node, config);

    case 'image-generation':
      return executeImageGenerationNode(node, config, input);

    case 'image-edit':
      return executeImageEditNode(node, config, input, edges, context);

    case 'image-upscaler':
      return executeImageUpscalerNode(node, config, input, edges, context);

    case 'text-to-video':
      return executeTextToVideoNode(node, config, input);

    case 'image-to-video':
      return executeImageToVideoNode(node, config, input, edges, context);

    case 'first-last-frame-video':
      return executeFirstLastFrameVideoNode(
        node,
        config,
        input,
        edges,
        context,
      );

    case 'references-to-video':
      return executeReferencesToVideoNode(node, config, input, edges, context);

    case 'video-merge':
      return executeVideoMergeNode(node, config, input, edges, context);

    case 'music-generation':
      return executeMusicGenerationNode(node, config, input, edges, context);

    case 'talking-video':
      return executeTalkingVideoNode(node, config, input, edges, context);

    case 'text-to-speech':
      return executeTextToSpeechNode(node, config, input, edges, context);

    case 'text-generation':
      return executeTextGenerationNode(node, config, input, edges, context);

    case 'text-preset':
      return executeTextPresetNode(node, config, input, edges, context);

    case 'output':
      return executeOutputNode(node, config, input);

    case 'image-splitter':
      return executeImageSplitterNode(node, config, input, edges, context);

    case 'note':
    case 'group':
      return null; // Notes and groups don't execute

    default:
      throw new Error(`Unknown node type: ${node.type}`);
  }
}

/**
 * Execute video merge node
 *
 * Expects one or more incoming video URLs (string or objects with videoUrl/url),
 * and returns the merged video URL from the backend.
 */
async function executeVideoMergeNode(
  node: Node,
  config: any,
  _input: any,
  edges: Edge[],
  context: ExecutionContext,
): Promise<any> {
  const incomingEdges = edges.filter((edge) => edge.target === node.id);

  const urlsBySource: Record<string, string> = {};

  for (const edge of incomingEdges) {
    const sourceResult = context.nodeResults[edge.source];

    if (!sourceResult) continue;

    if (typeof sourceResult === 'string') {
      if (
        sourceResult.startsWith('http://') ||
        sourceResult.startsWith('https://')
      ) {
        urlsBySource[edge.source] = sourceResult;
      }
    } else if (typeof sourceResult === 'object') {
      const obj = sourceResult as any;
      const url: string | undefined = obj.videoUrl || obj.video_url || obj.url;
      if (url) {
        urlsBySource[edge.source] = url;
      }
    }
  }

  const sourceIds = Object.keys(urlsBySource);
  const order = Array.isArray(config?.videoOrder)
    ? (config.videoOrder as string[])
    : [];

  let orderedUrls: string[] = [];

  if (order.length > 0) {
    // First, add URLs in the user-specified order
    orderedUrls = order
      .map((sourceId) => urlsBySource[sourceId])
      .filter((url): url is string => Boolean(url));

    // Then append any remaining sources not in the saved order
    sourceIds.forEach((sourceId) => {
      if (!order.includes(sourceId)) {
        const url = urlsBySource[sourceId];
        if (url && !orderedUrls.includes(url)) {
          orderedUrls.push(url);
        }
      }
    });
  } else {
    orderedUrls = sourceIds.map((sourceId) => urlsBySource[sourceId]);
  }

  const uniqueUrls = Array.from(new Set(orderedUrls));

  if (uniqueUrls.length < 2) {
    throw new Error(
      'Video merge node requires at least two connected video inputs.',
    );
  }

  const response = await fetch('/api/merge-videos', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ video_urls: uniqueUrls }),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Video merge failed');
  }

  const result = await response.json();
  // Normalize to an object with videoUrl for downstream consumers
  const videoUrl =
    result.videoUrl || result.video_url || result.video?.url || '';
  return {
    videoUrl,
    raw: result,
  };
}

/**
 * Get input from connected nodes
 */
function getNodeInput(
  nodeId: string,
  edges: Edge[],
  context: ExecutionContext,
): any {
  // Find all incoming edges
  const incomingEdges = edges.filter((edge) => edge.target === nodeId);

  if (incomingEdges.length === 0) {
    return null;
  }

  // If single input, return it directly
  if (incomingEdges.length === 1) {
    const sourceId = incomingEdges[0].source;
    return context.nodeResults[sourceId];
  }

  // If multiple inputs, return as array
  return incomingEdges.map((edge) => context.nodeResults[edge.source]);
}

/**
 * Execute input node
 */
async function executeInputNode(node: Node, config: any): Promise<any> {
  // Input nodes just return their configured value
  return config.value || config.prompt || '';
}

/**
 * Execute upload node
 */
async function executeUploadNode(node: Node, config: any): Promise<any> {
  // Upload nodes return the uploaded media URL
  const mediaType = config.mediaType;
  const imageUrl = config.imageUrl || '';
  const videoUrl = config.videoUrl || '';
  const audioUrl = config.audioUrl || '';

  if (mediaType === 'video') {
    if (!videoUrl) {
      throw new Error(
        'No video has been uploaded. Please upload a video first.',
      );
    }
    // Try to extract first/last frames for better downstream compatibility
    try {
      const framesRes = await fetch('/api/extract-video-frames', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ videoUrl }),
      });
      if (framesRes.ok) {
        const frames = await framesRes.json();
        return {
          videoUrl,
          firstFrame: frames.firstFrame,
          lastFrame: frames.lastFrame,
        };
      }
    } catch {
      // Ignore frame extraction failure and fall back to plain video URL
    }
    return videoUrl;
  }
  if (mediaType === 'audio') {
    if (!audioUrl) {
      throw new Error(
        'No audio has been uploaded. Please upload an audio file first.',
      );
    }
    return audioUrl;
  }

  // Default to image behavior
  const effectiveImageUrl = imageUrl || node.data?.result || '';
  if (!effectiveImageUrl) {
    throw new Error(
      'No image has been uploaded. Please upload an image first.',
    );
  }

  return effectiveImageUrl;
}

/**
 * Execute select-image node
 */
async function executeSelectImageNode(node: Node, config: any): Promise<any> {
  // Select-image nodes return the selected media URL
  const selectedIsVideo = config.selectedIsVideo || false;
  const mediaUrl = config.selectedImageUrl || node.data?.result || '';

  if (!mediaUrl) {
    throw new Error(
      selectedIsVideo
        ? 'No video has been selected. Please select a video from the gallery.'
        : 'No image has been selected. Please select an image from the gallery.',
    );
  }

  // For videos, try to extract first/last frames like upload node
  if (selectedIsVideo) {
    try {
      const framesRes = await fetch('/api/extract-video-frames', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ videoUrl: mediaUrl }),
      });
      if (framesRes.ok) {
        const frames = await framesRes.json();
        return {
          videoUrl: mediaUrl,
          firstFrame: frames.firstFrame,
          lastFrame: frames.lastFrame,
        };
      }
    } catch {
      // Ignore frame extraction failure and fall back to plain video URL
    }
    return mediaUrl;
  }

  return mediaUrl;
}

/**
 * Poll for image completion
 */
async function pollForImage(
  imageId: string,
  maxAttempts: number = 60,
): Promise<string> {
  for (let attempt = 0; attempt < maxAttempts; attempt++) {
    // Wait 5 seconds before checking (except first attempt)
    if (attempt > 0) {
      await new Promise((resolve) => setTimeout(resolve, 5000));
    }

    try {
      const response = await fetch('/api/images', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          action: 'GET',
          image_id: imageId,
        }),
      });

      if (!response.ok) {
        throw new Error('Failed to check image status');
      }

      const data = await response.json();

      // Check if image is ready
      if (data.image?.image_url) {
        return data.image.image_url;
      }
    } catch (error) {
      console.error('Error polling for image:', error);
      // Continue polling unless we've exhausted attempts
    }
  }

  throw new Error('Image generation timed out after 5 minutes');
}

/**
 * Execute image generation node
 */
async function executeImageGenerationNode(
  node: Node,
  config: any,
  input: any,
): Promise<any> {
  const prompt = config.prompt || input || '';

  // Resolve variables in prompt
  const resolvedPrompt = resolveVariables(
    prompt,
    useWorkflowStore.getState().nodeResults,
    useWorkflowStore.getState().nodes,
  );

  // Require a non-empty prompt (either typed or from connected nodes)
  if (!resolvedPrompt || String(resolvedPrompt).trim().length === 0) {
    throw new Error(
      'Prompt is required for the Text to Image node. Please enter a prompt or connect a Text node.',
    );
  }

  const payload = {
    prompt: resolvedPrompt,
    model: config.model || 'Nano Banana Pro',
    dimensions: config.dimensions || '1:1',
    outputs: config.outputs || 1,
    style: config.style || '',
    artist: config.artist || '',
    useHD: config.useHD || false,
    useVector: config.useVector || false,
  };

  // Call the image generation API
  const response = await fetch('/api/generate-image', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(payload),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Image generation failed');
  }

  const result = await response.json();

  // Handle different response formats
  // API returns array of image objects: [{ id, image_url, ... }]
  if (Array.isArray(result)) {
    const imageUrls: string[] = [];

    for (const img of result) {
      // If image_url is already present, use it
      if (img.image_url) {
        imageUrls.push(img.image_url);
      }
      // If we have an ID but no URL, poll for completion
      else if (img.id) {
        const url = await pollForImage(img.id);
        imageUrls.push(url);
      }
    }

    return imageUrls.length === 1 ? imageUrls[0] : imageUrls;
  }

  // Fallback for other formats
  return result.imageUrls || result.imageUrl || result.image_url || result;
}

/**
 * Execute image edit node
 */
async function executeImageEditNode(
  node: Node,
  config: any,
  _input: any, // unused, kept for signature compatibility
  edges: Edge[],
  context: ExecutionContext,
): Promise<any> {
  const imageUrls: string[] = [];
  let inputPrompt = '';

  // Get inputs based on target handles
  const incomingEdges = edges.filter((edge) => edge.target === node.id);

  for (const edge of incomingEdges) {
    const sourceResult = context.nodeResults[edge.source];

    if (edge.targetHandle === 'image') {
      // This is the image input - collect all images
      if (sourceResult) {
        const resolved = await getImageUrlFromSourceResult(sourceResult, edge);
        if (resolved) imageUrls.push(resolved);
      }
    } else if (edge.targetHandle === 'prompt') {
      // This is the prompt input
      inputPrompt = sourceResult;
    } else {
      // Legacy support: no handle specified
      // Guess based on content
      if (typeof sourceResult === 'string') {
        if (
          sourceResult.startsWith('http://') ||
          sourceResult.startsWith('https://')
        ) {
          imageUrls.push(sourceResult);
        } else {
          inputPrompt = sourceResult;
        }
      }
    }
  }

  // Validate we have at least one image URL
  if (imageUrls.length === 0) {
    throw new Error(
      'Image edit node requires at least one image URL connected to the "Image" input (purple handle)',
    );
  }

  // Prefer configured prompt (which may contain @NodeName references) over direct input
  // This allows users to type "@NodeName additional text" in the prompt field
  // Only use inputPrompt if config.prompt is empty (backward compatibility)
  const prompt = config.prompt || inputPrompt || '';

  // Resolve variables in prompt (replaces @NodeName with actual values)
  const resolvedPrompt = resolveVariables(
    prompt,
    useWorkflowStore.getState().nodeResults,
    useWorkflowStore.getState().nodes,
  );

  if (!resolvedPrompt || String(resolvedPrompt).trim().length === 0) {
    throw new Error(
      'Prompt is required for the Image to Image node. Please enter a prompt or connect a Text node.',
    );
  }

  // Build payload - use 'images' array if multiple images, 'image' if single
  const payload: any = {
    action: 'UPDATE_KONTEXT',
    prompt: resolvedPrompt,
    editModel: config.editModel || 'Nano Banana Pro',
    aspectRatio: config.dimensions || '1:1',
    resolution: config.resolution || '2K',
    useHD: config.useHD || false,
  };

  // Use 'images' field for multiple images, 'image' for single
  if (imageUrls.length > 1) {
    payload.images = JSON.stringify(imageUrls); // Stringify for API to parse
  } else {
    payload.image = imageUrls[0];
  }

  // Call the images API for editing
  const response = await fetch('/api/images', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(payload),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Image edit failed');
  }

  const result = await response.json();

  // Check if this is an async model that needs polling
  const isAsyncModel = [
    'Qwen Image',
    'FLUX.2 [pro]',
    'FLUX.2 [flex]',
    'Seedream 5.0 Lite',
    'Seedream 5.0 Pro',
    'Seedream 4.5',
    'Seedream 4.0',
    'Ideogram v3 Character',
    'Reve Fast',
    'Reve',
  ].includes(config.editModel || 'Seedream 4.0');

  // Handle different response formats
  if (result.images && Array.isArray(result.images)) {
    const imageUrls: string[] = [];

    for (const img of result.images) {
      // If image_url is already present, use it
      if (img.image_url) {
        imageUrls.push(img.image_url);
      }
      // If we have an ID but no URL and it's an async model, poll for completion
      else if (img.id && isAsyncModel) {
        const url = await pollForImage(img.id);
        imageUrls.push(url);
      } else if (img.id) {
        // Try polling anyway if we only have an ID
        const url = await pollForImage(img.id);
        imageUrls.push(url);
      }
    }

    return imageUrls.length === 1 ? imageUrls[0] : imageUrls;
  }

  // Single image response
  if (result.image) {
    return result.image;
  }

  // Fallback
  return result.image_url || result;
}

/**
 * Poll for video completion by checking the database
 */
async function pollForVideo(
  videoId: number,
  maxAttempts: number = 120,
): Promise<string> {
  for (let attempt = 0; attempt < maxAttempts; attempt++) {
    // Wait 5 seconds before checking (except first attempt)
    if (attempt > 0) {
      await new Promise((resolve) => setTimeout(resolve, 5000));
    }

    try {
      // Fetch all videos to check if this one is complete
      const response = await fetch('/api/get-videos');

      if (!response.ok) {
        throw new Error('Failed to check video status');
      }

      const videos = await response.json();

      // Find our video by ID
      const video = videos.find((v: any) => v.id === videoId);

      // Check if video is ready (image_url is populated)
      if (video && video.image_url && video.image_url !== '') {
        return video.image_url;
      }
    } catch (error) {
      console.error('Error polling for video:', error);
      // Continue polling unless we've exhausted attempts
    }
  }

  throw new Error('Video generation timed out after 10 minutes');
}

/**
 * Execute image upscaler node
 */
async function executeImageUpscalerNode(
  node: Node,
  config: any,
  _input: any,
  edges: Edge[],
  context: ExecutionContext,
): Promise<any> {
  // Get image URL from connected node
  const incomingEdges = edges.filter((edge) => edge.target === node.id);

  let imageUrl = '';

  for (const edge of incomingEdges) {
    const sourceResult = context.nodeResults[edge.source];

    if (edge.targetHandle === 'image') {
      if (sourceResult) {
        const resolved = await getImageUrlFromSourceResult(sourceResult, edge);
        if (resolved) imageUrl = resolved;
      }
    } else {
      // Legacy support: use getImageUrlFromSourceResult to handle video URL frame extraction
      if (sourceResult) {
        const resolved = await getImageUrlFromSourceResult(sourceResult, edge);
        if (resolved) imageUrl = resolved;
      }
    }
  }

  // Validate we have an image URL
  if (!imageUrl) {
    throw new Error(
      'Image upscaler node requires an image URL connected to the "Image" input (purple handle)',
    );
  }

  // Build payload based on the upscaler mode
  const payload: any = {
    image: imageUrl,
    mode: config.mode || 'Crisp',
    scaleFactor: config.scaleFactor || 2,
  };

  // Add creative mode parameters if mode is Creative
  if (config.mode === 'Creative') {
    payload.creativity = config.creativity ?? 3;
    payload.HDR = config.HDR ?? 6;
    payload.resemblance = config.resemblance ?? 6;
    payload.fractality = config.fractality ?? 6;
  }

  // Call the magic upscaler API
  const response = await fetch('/api/magic-upscaler', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(payload),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Image upscaling failed');
  }

  const result = await response.json();

  // Return the upscaled image URL
  return result.data?.image_url || result.data || result.image_url;
}

/**
 * Execute text-to-video generation node
 */
async function executeTextToVideoNode(
  node: Node,
  config: any,
  input: any,
): Promise<any> {
  const prompt = config.prompt || input || '';

  const resolvedPrompt = resolveVariables(
    prompt,
    useWorkflowStore.getState().nodeResults,
    useWorkflowStore.getState().nodes,
  );

  if (!resolvedPrompt || String(resolvedPrompt).trim().length === 0) {
    throw new Error(
      'Prompt is required for the Text to Video node. Please enter a prompt or connect a Text node.',
    );
  }

  const payload: Record<string, unknown> = {
    prompt: resolvedPrompt,
    model: config.model || 'Veo 3.1 Fast',
    duration: config.duration || 5,
    aspectRatio: config.aspectRatio || '16:9',
  };

  if (config.resolution) {
    payload.resolution = config.resolution;
  }

  if (config.generateAudio !== undefined) {
    payload.generateAudio = config.generateAudio;
  }

  // Call the video generation API
  const response = await fetch('/api/generate-video', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(payload),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Text-to-video generation failed');
  }

  const result = await response.json();

  // The API returns a video object with id and image_url (initially empty)
  // We need to poll until image_url is populated
  if (result && result.id) {
    // If image_url is already populated, return it
    if (result.image_url && result.image_url !== '') {
      // Extract frames
      try {
        const framesRes = await fetch('/api/extract-video-frames', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ videoUrl: result.image_url }),
        });
        if (framesRes.ok) {
          const frames = await framesRes.json();
          return {
            videoUrl: result.image_url,
            firstFrame: frames.firstFrame,
            lastFrame: frames.lastFrame,
          };
        }
      } catch {
        // ignore frame extraction failure, return video only
      }
      return {
        videoUrl: result.image_url,
      };
    }

    // Otherwise poll for completion
    const videoUrl = await pollForVideo(result.id);
    // Extract frames
    try {
      const framesRes = await fetch('/api/extract-video-frames', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ videoUrl }),
      });
      if (framesRes.ok) {
        const frames = await framesRes.json();
        return {
          videoUrl,
          firstFrame: frames.firstFrame,
          lastFrame: frames.lastFrame,
        };
      }
    } catch {
      // ignore frame extraction failure, return video only
    }
    return { videoUrl };
  }

  // Fallback for unexpected formats
  const fallbackUrl =
    result.videoUrl ||
    result.video_url ||
    result.url ||
    result.image_url ||
    result;
  if (typeof fallbackUrl === 'string') {
    try {
      const framesRes = await fetch('/api/extract-video-frames', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ videoUrl: fallbackUrl }),
      });
      if (framesRes.ok) {
        const frames = await framesRes.json();
        return {
          videoUrl: fallbackUrl,
          firstFrame: frames.firstFrame,
          lastFrame: frames.lastFrame,
        };
      }
    } catch {
      // ignore
    }
  }
  return fallbackUrl;
}

/**
 * Execute image-to-video generation node
 */
async function executeImageToVideoNode(
  node: Node,
  config: any,
  _input: any, // unused, kept for signature compatibility
  edges: Edge[],
  context: ExecutionContext,
): Promise<any> {
  let imageUrl = '';
  let inputPrompt = '';

  // Get inputs based on target handles
  const incomingEdges = edges.filter((edge) => edge.target === node.id);

  for (const edge of incomingEdges) {
    const sourceResult = context.nodeResults[edge.source];

    if (edge.targetHandle === 'image') {
      // This is the image input
      if (
        sourceResult &&
        typeof sourceResult === 'object' &&
        (sourceResult.firstFrame || sourceResult.lastFrame)
      ) {
        // Prefer source handle if available
        const srcHandle = (edge as any).sourceHandle as string | undefined;
        if (srcHandle === 'last-frame' && sourceResult.lastFrame) {
          imageUrl = sourceResult.lastFrame;
        } else if (srcHandle === 'first-frame' && sourceResult.firstFrame) {
          imageUrl = sourceResult.firstFrame;
        } else {
          imageUrl = sourceResult.firstFrame || sourceResult.lastFrame || '';
        }
      } else {
        // Use getImageUrlFromSourceResult to handle video URL frame extraction
        const extractedUrl = await getImageUrlFromSourceResult(
          sourceResult,
          edge,
        );
        imageUrl = extractedUrl || sourceResult;
      }
    } else if (edge.targetHandle === 'prompt') {
      // This is the prompt input
      inputPrompt = sourceResult;
    } else {
      // Legacy support: no handle specified
      // Guess based on content
      if (typeof sourceResult === 'string') {
        if (
          sourceResult.startsWith('http://') ||
          sourceResult.startsWith('https://')
        ) {
          // Use getImageUrlFromSourceResult to handle video URL frame extraction
          const extractedUrl = await getImageUrlFromSourceResult(
            sourceResult,
            edge,
          );
          imageUrl = extractedUrl || sourceResult;
        } else {
          inputPrompt = sourceResult;
        }
      }
    }
  }

  // Validate we have an image URL
  if (!imageUrl) {
    throw new Error(
      'Image-to-video node requires an image URL connected to the "Image" input (purple handle)',
    );
  }

  // Prefer configured prompt (which may contain @NodeName references) over direct input
  // This allows users to type "@NodeName additional text" in the prompt field
  // Only use inputPrompt if config.prompt is empty (backward compatibility)
  const prompt = config.prompt || inputPrompt || '';

  // Resolve variables in prompt (replaces @NodeName with actual values)
  const resolvedPrompt = resolveVariables(
    prompt,
    useWorkflowStore.getState().nodeResults,
    useWorkflowStore.getState().nodes,
  );

  const payload: Record<string, unknown> = {
    prompt: resolvedPrompt,
    model: config.model || 'Veo 3.1 Fast Image',
    duration: config.duration || 5,
    aspectRatio: config.aspectRatio || '16:9',
    image: imageUrl,
  };

  if (config.resolution) {
    payload.resolution = config.resolution;
  }

  if (config.generateAudio !== undefined) {
    payload.generateAudio = config.generateAudio;
  }

  // Call the video generation API
  const response = await fetch('/api/generate-video', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(payload),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Image-to-video generation failed');
  }

  const result = await response.json();

  // The API returns a video object with id and image_url (initially empty)
  // We need to poll until image_url is populated
  if (result && result.id) {
    // If image_url is already populated, return it
    if (result.image_url && result.image_url !== '') {
      // Extract frames
      try {
        const framesRes = await fetch('/api/extract-video-frames', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ videoUrl: result.image_url }),
        });
        if (framesRes.ok) {
          const frames = await framesRes.json();
          return {
            videoUrl: result.image_url,
            firstFrame: frames.firstFrame,
            lastFrame: frames.lastFrame,
          };
        }
      } catch {
        // ignore frame extraction failure, return video only
      }
      return { videoUrl: result.image_url };
    }

    // Otherwise poll for completion
    const videoUrl = await pollForVideo(result.id);
    try {
      const framesRes = await fetch('/api/extract-video-frames', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ videoUrl }),
      });
      if (framesRes.ok) {
        const frames = await framesRes.json();
        return {
          videoUrl,
          firstFrame: frames.firstFrame,
          lastFrame: frames.lastFrame,
        };
      }
    } catch {
      // ignore
    }
    return { videoUrl };
  }

  // Fallback for unexpected formats
  const fallbackUrl =
    result.videoUrl ||
    result.video_url ||
    result.url ||
    result.image_url ||
    result;
  if (typeof fallbackUrl === 'string') {
    try {
      const framesRes = await fetch('/api/extract-video-frames', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ videoUrl: fallbackUrl }),
      });
      if (framesRes.ok) {
        const frames = await framesRes.json();
        return {
          videoUrl: fallbackUrl,
          firstFrame: frames.firstFrame,
          lastFrame: frames.lastFrame,
        };
      }
    } catch {
      // ignore
    }
  }
  return fallbackUrl;
}

/**
 * Execute first-last-frame-to-video generation node
 */
async function executeFirstLastFrameVideoNode(
  node: Node,
  config: any,
  _input: any, // unused, kept for signature compatibility
  edges: Edge[],
  context: ExecutionContext,
): Promise<any> {
  let firstFrameUrl = '';
  let lastFrameUrl = '';
  let inputPrompt = '';

  // Get inputs based on target handles
  const incomingEdges = edges.filter((edge) => edge.target === node.id);

  for (const edge of incomingEdges) {
    const sourceResult = context.nodeResults[edge.source];

    if (edge.targetHandle === 'first-frame') {
      // This is the first frame image input
      const resolved = await getImageUrlFromSourceResult(sourceResult, edge);
      if (resolved) {
        firstFrameUrl = resolved;
      }
    } else if (edge.targetHandle === 'last-frame') {
      // This is the last frame image input
      const resolved = await getImageUrlFromSourceResult(sourceResult, edge);
      if (resolved) {
        lastFrameUrl = resolved;
      }
    } else if (edge.targetHandle === 'prompt') {
      // This is the prompt input
      inputPrompt = sourceResult;
    } else {
      // Legacy support: no handle specified
      // Try to guess based on content - not reliable for this node type
      if (typeof sourceResult === 'string') {
        if (
          sourceResult.startsWith('http://') ||
          sourceResult.startsWith('https://')
        ) {
          // Could be either frame, assign to first if empty
          if (!firstFrameUrl) {
            firstFrameUrl = sourceResult;
          } else if (!lastFrameUrl) {
            lastFrameUrl = sourceResult;
          }
        } else {
          inputPrompt = sourceResult;
        }
      }
    }
  }

  // Validate we have both frame URLs
  if (!firstFrameUrl) {
    throw new Error(
      'First-last-frame-video node requires a first frame image URL connected to the "first-frame" input (purple handle at 30%)',
    );
  }

  if (!lastFrameUrl) {
    throw new Error(
      'First-last-frame-video node requires a last frame image URL connected to the "last-frame" input (purple handle at 70%)',
    );
  }

  // Prefer configured prompt (which may contain @NodeName references) over direct input
  // This allows users to type "@NodeName additional text" in the prompt field
  // Only use inputPrompt if config.prompt is empty (backward compatibility)
  const prompt = config.prompt || inputPrompt || '';

  // Resolve variables in prompt (replaces @NodeName with actual values)
  const resolvedPrompt = resolveVariables(
    prompt,
    useWorkflowStore.getState().nodeResults,
    useWorkflowStore.getState().nodes,
  );

  if (!resolvedPrompt || String(resolvedPrompt).trim().length === 0) {
    throw new Error(
      'Prompt is required for the First-Last Frame to Video node. Please enter a prompt or connect a Text node.',
    );
  }

  // Build payload with both images
  // Note: We send tailImage as a standardized parameter name
  // The API will map it to the model-specific field name internally
  const payload: Record<string, unknown> = {
    prompt: resolvedPrompt,
    model: config.model || 'Veo 3.1 Fast First-Last Frame',
    duration: config.duration || 8,
    aspectRatio: config.aspectRatio || '16:9',
    image: firstFrameUrl,
    tailImage: lastFrameUrl,
  };

  if (config.resolution) {
    payload.resolution = config.resolution;
  }

  if (config.generateAudio !== undefined) {
    payload.generateAudio = config.generateAudio;
  }

  // Call the video generation API
  const response = await fetch('/api/generate-video', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(payload),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'First-last-frame-video generation failed');
  }

  const result = await response.json();

  // The API returns a video object with id and image_url (initially empty)
  // We need to poll until image_url is populated
  if (result && result.id) {
    // If image_url is already populated, return it
    if (result.image_url && result.image_url !== '') {
      // Extract frames
      try {
        const framesRes = await fetch('/api/extract-video-frames', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ videoUrl: result.image_url }),
        });
        if (framesRes.ok) {
          const frames = await framesRes.json();
          return {
            videoUrl: result.image_url,
            firstFrame: frames.firstFrame,
            lastFrame: frames.lastFrame,
          };
        }
      } catch {
        // ignore extraction failure
      }
      return { videoUrl: result.image_url };
    }

    // Otherwise poll for completion
    const videoUrl = await pollForVideo(result.id);
    try {
      const framesRes = await fetch('/api/extract-video-frames', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ videoUrl }),
      });
      if (framesRes.ok) {
        const frames = await framesRes.json();
        return {
          videoUrl,
          firstFrame: frames.firstFrame,
          lastFrame: frames.lastFrame,
        };
      }
    } catch {
      // ignore
    }
    return { videoUrl };
  }

  // Fallback for unexpected formats
  const fallbackUrl =
    result.videoUrl ||
    result.video_url ||
    result.url ||
    result.image_url ||
    result;
  if (typeof fallbackUrl === 'string') {
    try {
      const framesRes = await fetch('/api/extract-video-frames', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ videoUrl: fallbackUrl }),
      });
      if (framesRes.ok) {
        const frames = await framesRes.json();
        return {
          videoUrl: fallbackUrl,
          firstFrame: frames.firstFrame,
          lastFrame: frames.lastFrame,
        };
      }
    } catch {
      // ignore
    }
  }
  return fallbackUrl;
}

/**
 * Execute references-to-video generation node.
 *
 * Collects up to N reference images from connected nodes (handles `reference-1`...
 * `reference-N`) and an optional prompt, then calls /api/generate-video with the
 * `referenceImages` payload supported by reference-capable video models.
 */
async function executeReferencesToVideoNode(
  node: Node,
  config: any,
  _input: any,
  edges: Edge[],
  context: ExecutionContext,
): Promise<any> {
  const refConfig = getReferenceImageConfigForModel(config.model || '');
  const referenceType: ReferenceImageType =
    (config.referenceType as ReferenceImageType) ||
    refConfig?.supportedTypes[0] ||
    'character';

  const incomingEdges = edges.filter((edge) => edge.target === node.id);

  const referenceUrls: string[] = [];
  let inputPrompt = '';

  for (const edge of incomingEdges) {
    const sourceResult = context.nodeResults[edge.source];
    const handleId = String(edge.targetHandle || '');

    if (handleId === 'references') {
      const url = await getImageUrlFromSourceResult(sourceResult, edge);
      if (url) referenceUrls.push(url);
    } else if (handleId === 'prompt' || handleId === 'text') {
      if (typeof sourceResult === 'string') inputPrompt = sourceResult;
    }
  }

  if (referenceUrls.length === 0) {
    throw new Error(
      'References to Video node requires at least one reference image connected to the "References" input.',
    );
  }

  const maxImages = refConfig?.maxImages ?? referenceUrls.length;
  const trimmedRefs = referenceUrls.slice(0, maxImages);

  const referenceImages = trimmedRefs.map((url) => ({
    id: uuidv4(),
    url,
    type: referenceType,
  }));

  const prompt = config.prompt || inputPrompt || '';
  const resolvedPrompt = resolveVariables(
    prompt,
    useWorkflowStore.getState().nodeResults,
    useWorkflowStore.getState().nodes,
  );

  const payload: Record<string, unknown> = {
    prompt: resolvedPrompt,
    model: config.model,
    duration: config.duration || 5,
    aspectRatio: config.aspectRatio || '16:9',
    referenceImages,
  };

  if (config.resolution) {
    payload.resolution = config.resolution;
  }

  if (config.generateAudio !== undefined) {
    payload.generateAudio = config.generateAudio;
  }

  const response = await fetch('/api/generate-video', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'References-to-video generation failed');
  }

  const result = await response.json();

  if (result && result.id) {
    if (result.image_url && result.image_url !== '') {
      try {
        const framesRes = await fetch('/api/extract-video-frames', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ videoUrl: result.image_url }),
        });
        if (framesRes.ok) {
          const frames = await framesRes.json();
          return {
            videoUrl: result.image_url,
            firstFrame: frames.firstFrame,
            lastFrame: frames.lastFrame,
          };
        }
      } catch {
        // ignore frame extraction failure
      }
      return { videoUrl: result.image_url };
    }

    const videoUrl = await pollForVideo(result.id);
    try {
      const framesRes = await fetch('/api/extract-video-frames', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ videoUrl }),
      });
      if (framesRes.ok) {
        const frames = await framesRes.json();
        return {
          videoUrl,
          firstFrame: frames.firstFrame,
          lastFrame: frames.lastFrame,
        };
      }
    } catch {
      // ignore
    }
    return { videoUrl };
  }

  const fallbackUrl =
    result.videoUrl ||
    result.video_url ||
    result.url ||
    result.image_url ||
    result;
  if (typeof fallbackUrl === 'string') {
    try {
      const framesRes = await fetch('/api/extract-video-frames', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ videoUrl: fallbackUrl }),
      });
      if (framesRes.ok) {
        const frames = await framesRes.json();
        return {
          videoUrl: fallbackUrl,
          firstFrame: frames.firstFrame,
          lastFrame: frames.lastFrame,
        };
      }
    } catch {
      // ignore
    }
  }
  return fallbackUrl;
}

/**
 * Execute text-to-speech node
 */
async function executeTextToSpeechNode(
  node: Node,
  config: any,
  _input: any,
  edges: Edge[],
  context: ExecutionContext,
): Promise<any> {
  let inputText = '';

  // Get preset slug and fields from config
  const presetSlug = config.presetSlug;
  const fields: Map<string, any> =
    config.fields instanceof Map
      ? new Map<string, any>(config.fields as Map<string, any>)
      : new Map<string, any>(
          Object.entries((config.fields as Record<string, any>) || {}),
        );

  // Get text input from connected node
  const incomingEdges = edges.filter((edge) => edge.target === node.id);

  for (const edge of incomingEdges) {
    const sourceResult = context.nodeResults[edge.source];

    if (edge.targetHandle === 'text') {
      inputText = sourceResult;
    } else if (
      edge.targetHandle === 'extra1' ||
      edge.targetHandle === 'extra2'
    ) {
      fields.set(edge.targetHandle, sourceResult);
    } else {
      // Legacy support: assume it's text
      if (
        typeof sourceResult === 'string' &&
        !sourceResult.startsWith('http')
      ) {
        inputText = sourceResult;
      }
    }
  }

  // Use text from input if available, otherwise use configured text
  const text = inputText || config.text || '';

  if (!text || text.trim() === '') {
    throw new Error('Text-to-speech node requires text input');
  }

  // Resolve variables in text
  const resolvedText = resolveVariables(
    text,
    useWorkflowStore.getState().nodeResults,
    useWorkflowStore.getState().nodes,
  );

  // Build payload
  const payload = {
    text: resolvedText,
    voiceID: config.voiceID || 'gUABw7pXQjhjt0kNFBTF', // Default to Antoni
  };

  // Call the generate-audio API
  const response = await fetch('/api/generate-audio', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(payload),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Text-to-speech generation failed');
  }

  const result = await response.json();

  // Return the audio URL
  return result.audio_url || result.url || result;
}

/**
 * Execute text generation node
 */
async function executeTextGenerationNode(
  node: Node,
  config: any,
  _input: any, // unused, kept for signature compatibility
  edges: Edge[],
  context: ExecutionContext,
): Promise<any> {
  const imageUrls: string[] = [];
  let inputPrompt = '';

  // Get inputs based on target handles
  const incomingEdges = edges.filter((edge) => edge.target === node.id);

  for (const edge of incomingEdges) {
    const sourceResult = context.nodeResults[edge.source];

    if (edge.targetHandle === 'image') {
      // This is the image input - collect all images
      if (sourceResult) {
        const resolved = await getImageUrlFromSourceResult(sourceResult, edge);
        if (resolved) imageUrls.push(resolved);
      }
    } else if (edge.targetHandle === 'prompt') {
      // This is the prompt input
      inputPrompt = sourceResult;
    } else {
      // Legacy support: no handle specified
      // Guess based on content
      if (typeof sourceResult === 'string') {
        if (
          sourceResult.startsWith('http://') ||
          sourceResult.startsWith('https://')
        ) {
          imageUrls.push(sourceResult);
        } else {
          inputPrompt = sourceResult;
        }
      }
    }
  }

  // Prefer configured prompt (which may contain @NodeName references) over direct input
  // This allows users to type "@NodeName additional text" in the prompt field
  // Only use inputPrompt if config.prompt is empty (backward compatibility)
  const prompt = config.prompt || inputPrompt || '';

  // Resolve variables in prompt (replaces @NodeName with actual values)
  const resolvedPrompt = resolveVariables(
    prompt,
    useWorkflowStore.getState().nodeResults,
    useWorkflowStore.getState().nodes,
  );

  // Build payload
  const payload: any = {
    prompt: resolvedPrompt,
    model: config.model || 'gpt-5.4-instant',
    systemPrompt: config.systemPrompt,
  };

  // Add images if any
  if (imageUrls.length > 0) {
    payload.images = imageUrls;
  }

  // Call the text generation API
  const response = await fetch('/api/generate-text', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(payload),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Text generation failed');
  }

  const result = await response.json();

  return result.text || result.completion || result;
}

/**
 * Execute text preset node
 */
async function executeTextPresetNode(
  node: Node,
  config: any,
  _input: any,
  edges: Edge[],
  context: ExecutionContext,
): Promise<any> {
  let inputText = '';
  let extra1Input: any;
  let extra2Input: any;

  const presetSlug = config.presetSlug;
  const fields =
    config.fields instanceof Map
      ? new Map(config.fields)
      : new Map(Object.entries((config.fields as Record<string, any>) || {}));

  const workflowNodes = useWorkflowStore.getState().nodes;

  // Get text input from connected node
  const incomingEdges = edges.filter((edge) => edge.target === node.id);

  for (const edge of incomingEdges) {
    const sourceResult = context.nodeResults[edge.source];

    if (edge.targetHandle === 'text') {
      inputText = sourceResult;
    } else if (
      edge.targetHandle === 'extra1' ||
      edge.targetHandle === 'extra2'
    ) {
      if (edge.targetHandle === 'extra1') {
        extra1Input = sourceResult;
      } else {
        extra2Input = sourceResult;
      }
    } else {
      // Legacy support: assume it's text
      if (
        typeof sourceResult === 'string' &&
        !sourceResult.startsWith('http')
      ) {
        inputText = sourceResult;
      }
    }
  }

  if (!presetSlug) {
    throw new Error('Text preset node requires a preset to be selected');
  }

  // If we have input text, use it for the keywords field only when empty
  if (inputText) {
    const existingKeywords = fields.get('keywords');
    if (!existingKeywords || !String(existingKeywords).trim()) {
      fields.set('keywords', inputText);
    }
  }

  if (typeof extra1Input !== 'undefined') {
    const existingExtra1 = fields.get('extra1');
    if (!existingExtra1 || !String(existingExtra1).trim()) {
      fields.set('extra1', extra1Input);
    }
  }

  if (typeof extra2Input !== 'undefined') {
    const existingExtra2 = fields.get('extra2');
    if (!existingExtra2 || !String(existingExtra2).trim()) {
      fields.set('extra2', extra2Input);
    }
  }

  // Resolve @Node references in all string fields using node results
  const resolvedFields = new Map<string, any>();
  for (const [rawKey, value] of fields.entries()) {
    const key = String(rawKey);
    if (typeof value === 'string') {
      resolvedFields.set(
        key,
        resolveVariables(
          value,
          context.nodeResults as Record<string, any>,
          workflowNodes,
        ),
      );
    } else {
      resolvedFields.set(key, value);
    }
  }

  // Validate that keywords field is not empty
  const keywords = resolvedFields.get('keywords');
  if (!keywords || keywords.trim() === '') {
    throw new Error(
      'Text preset node requires keywords/input text. Please connect a text input or fill in the keywords field.',
    );
  }

  // Build payload for the /api/generate endpoint
  const payload: any = {
    preset: presetSlug,
    keywords: resolvedFields.get('keywords') || '',
    tone: resolvedFields.get('tone') || '',
    language: resolvedFields.get('language') || 'English',
    outputs: 1, // Generate single output for workflows
  };

  // Add all extra fields (extra1, extra2, etc.)
  const fieldEntries = Array.from(resolvedFields.entries());

  fieldEntries.forEach(([key, value]) => {
    if (key !== 'keywords' && key !== 'tone' && key !== 'language') {
      payload[key] = value;
    }
  });

  // Add common optional fields
  if (resolvedFields.get('length')) {
    payload.length = resolvedFields.get('length');
  }

  // Call the /api/generate endpoint
  const response = await fetch('/api/generate', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(payload),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Text preset generation failed');
  }

  const result = await response.json();

  // The API returns an array of completions: [{ id, text }, ...]
  // Extract the text from the first completion
  if (Array.isArray(result) && result.length > 0) {
    return result[0].text || '';
  }

  return result.text || result.completion || result;
}

/**
 * Execute output node
 */
async function executeOutputNode(
  node: Node,
  config: any,
  input: any,
): Promise<any> {
  // Output nodes just pass through their input
  return input;
}

/**
 * Execute image splitter node
 */
async function executeImageSplitterNode(
  node: Node,
  config: any,
  _input: any,
  edges: Edge[],
  context: ExecutionContext,
): Promise<any> {
  // Get image URL from connected node
  const incomingEdges = edges.filter((edge) => edge.target === node.id);

  let imageUrl = '';

  for (const edge of incomingEdges) {
    const sourceResult = context.nodeResults[edge.source];

    if (edge.targetHandle === 'image') {
      if (sourceResult) {
        const resolved = await getImageUrlFromSourceResult(sourceResult, edge);
        if (resolved) imageUrl = resolved;
      }
    } else {
      // Legacy support: use getImageUrlFromSourceResult to handle video URL frame extraction
      if (sourceResult) {
        const resolved = await getImageUrlFromSourceResult(sourceResult, edge);
        if (resolved) imageUrl = resolved;
      }
    }
  }

  // Validate we have an image URL
  if (!imageUrl) {
    throw new Error(
      'Image splitter node requires an image URL connected to the "Image" input (purple handle)',
    );
  }

  // Build payload
  const payload = {
    imageUrl,
    rows: config.rows || 2,
    columns: config.columns || 2,
  };

  // Call the split-image API
  const response = await fetch('/api/split-image', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(payload),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Image splitting failed');
  }

  const result = await response.json();

  // Return array of image URLs
  if (result.images && Array.isArray(result.images)) {
    return result.images.length === 1 ? result.images[0] : result.images;
  }

  return result;
}

/**
 * Poll for music/song completion by checking the speech status
 */
async function pollForMusic(
  speechId: number,
  maxAttempts: number = 60,
): Promise<string> {
  for (let attempt = 0; attempt < maxAttempts; attempt++) {
    if (attempt > 0) {
      await new Promise((resolve) => setTimeout(resolve, 10000));
    }

    try {
      const response = await fetch(`/api/get-speech-status?id=${speechId}`);

      if (!response.ok) {
        throw new Error('Failed to check music generation status');
      }

      const data = await response.json();

      if (data.status === 'completed' && data.url) {
        return data.url;
      }

      if (data.status === 'failed') {
        throw new Error(data.error_message || 'Music generation failed');
      }
    } catch (error: any) {
      // Only swallow transient network errors; re-throw everything else
      const msg = error?.message || '';
      const isTransient =
        msg.includes('Failed to check music generation status') ||
        msg.includes('fetch') ||
        msg.includes('network');
      if (!isTransient) {
        throw error;
      }
      console.error('Error polling for music:', error);
    }
  }

  throw new Error('Music generation timed out after 10 minutes');
}

/**
 * Execute music generation node
 */
async function executeMusicGenerationNode(
  node: Node,
  config: any,
  _input: any,
  edges: Edge[],
  context: ExecutionContext,
): Promise<any> {
  let inputText = '';

  // Get text input from connected node
  const incomingEdges = edges.filter((edge) => edge.target === node.id);

  for (const edge of incomingEdges) {
    const sourceResult = context.nodeResults[edge.source];

    if (edge.targetHandle === 'text') {
      inputText = sourceResult;
    } else if (
      typeof sourceResult === 'string' &&
      !sourceResult.startsWith('http')
    ) {
      inputText = sourceResult;
    }
  }

  const isCustomMode = config.isCustomMode || false;
  const isInstrumental = config.isInstrumental || false;
  const vocalGender = config.vocalGender || '';

  let payload: Record<string, any>;

  if (isCustomMode) {
    const style = config.songStyle || '';
    const lyrics = config.songLyrics || '';

    if (!style.trim()) {
      throw new Error('Music Generation node requires a style in Custom Mode.');
    }
    if (!lyrics.trim()) {
      throw new Error('Music Generation node requires lyrics in Custom Mode.');
    }

    payload = {
      style: resolveVariables(
        style,
        useWorkflowStore.getState().nodeResults,
        useWorkflowStore.getState().nodes,
      ),
      lyrics: resolveVariables(
        lyrics,
        useWorkflowStore.getState().nodeResults,
        useWorkflowStore.getState().nodes,
      ),
      customMode: true,
      instrumental: isInstrumental,
      useVersion2: true,
      model: 'V4',
      ...(vocalGender && !isInstrumental ? { vocalGender } : {}),
    };
  } else {
    const prompt = inputText || config.prompt || '';
    const resolvedPrompt = resolveVariables(
      prompt,
      useWorkflowStore.getState().nodeResults,
      useWorkflowStore.getState().nodes,
    );

    if (!resolvedPrompt || String(resolvedPrompt).trim().length === 0) {
      throw new Error(
        'Music Generation node requires a prompt or connected text input.',
      );
    }

    payload = {
      prompt: resolvedPrompt,
      customMode: false,
      instrumental: isInstrumental,
      useVersion2: true,
      model: 'V4',
      ...(vocalGender && !isInstrumental ? { vocalGender } : {}),
    };
  }

  const response = await fetch('/api/generate-song', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(payload),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Music generation failed');
  }

  const result = await response.json();

  // The API may return a single Speech object or an array
  const speech = Array.isArray(result) ? result[0] : result;

  // The API returns a Speech record with status 'processing'
  // We need to poll until the audio URL is available
  if (speech && speech.id) {
    return pollForMusic(speech.id);
  }

  throw new Error('Unexpected response from music generation API');
}

/**
 * Poll for talking video completion by checking the talking videos endpoint.
 * Talking videos go through WaveSpeed or VEED Fabric webhooks, which update
 * the underlying image record once the lip-synced output is ready.
 */
async function pollForTalkingVideo(
  videoId: number,
  maxAttempts: number = 120,
): Promise<string> {
  for (let attempt = 0; attempt < maxAttempts; attempt++) {
    if (attempt > 0) {
      await new Promise((resolve) => setTimeout(resolve, 5000));
    }

    try {
      const response = await fetch('/api/get-talking-videos');

      if (!response.ok) {
        throw new Error('Failed to check talking video status');
      }

      const videos = await response.json();
      const video = Array.isArray(videos)
        ? videos.find((v: any) => v.id === videoId)
        : null;

      if (video && video.image_url && video.image_url !== '') {
        return video.image_url;
      }
    } catch (error) {
      console.error('Error polling for talking video:', error);
    }
  }

  throw new Error('Talking video generation timed out after 10 minutes');
}

/**
 * Resolve an audio-typed source result into a usable audio URL.
 */
function getAudioUrlFromSourceResult(sourceResult: any): string | null {
  if (!sourceResult) return null;
  if (typeof sourceResult === 'string') {
    return sourceResult.startsWith('http') ? sourceResult : null;
  }
  if (typeof sourceResult === 'object') {
    const obj = sourceResult as Record<string, unknown>;
    const candidate =
      (obj.audioUrl as string | undefined) ||
      (obj.audio_url as string | undefined) ||
      (obj.url as string | undefined);
    return typeof candidate === 'string' && candidate.startsWith('http')
      ? candidate
      : null;
  }
  return null;
}

/**
 * Resolve a video-typed source result into a usable video URL.
 */
function getVideoUrlFromSourceResult(sourceResult: any): string | null {
  if (!sourceResult) return null;
  if (typeof sourceResult === 'string') {
    return sourceResult.startsWith('http') ? sourceResult : null;
  }
  if (typeof sourceResult === 'object') {
    const obj = sourceResult as Record<string, unknown>;
    const candidate =
      (obj.videoUrl as string | undefined) ||
      (obj.video_url as string | undefined) ||
      (obj.url as string | undefined);
    return typeof candidate === 'string' && candidate.startsWith('http')
      ? candidate
      : null;
  }
  return null;
}

/**
 * Execute talking video node.
 *
 * Inputs (by handle):
 *   - "image": still image of the speaker (purple)
 *   - "video": short video clip of the speaker (cyan)
 *   - "audio": audio clip of the speech (indigo, optional)
 *   - "text":  text to be spoken (blue, optional - requires voiceID in config)
 *
 * Either an image or a video must be provided. Either audio, or text + voice
 * must be provided. The node POSTs to /api/generate-talking-video and polls
 * for the final lip-synced video URL.
 */
async function executeTalkingVideoNode(
  node: Node,
  config: any,
  _input: any,
  edges: Edge[],
  context: ExecutionContext,
): Promise<any> {
  const incomingEdges = edges.filter((edge) => edge.target === node.id);

  let imageUrl = '';
  let videoUrl = '';
  let audioUrl = '';
  let inputText = '';

  for (const edge of incomingEdges) {
    const sourceResult = context.nodeResults[edge.source];
    if (sourceResult === undefined || sourceResult === null) continue;

    const handle = String(edge.targetHandle || '');

    if (handle === 'image') {
      const resolved = await getImageUrlFromSourceResult(sourceResult, edge);
      if (resolved) imageUrl = resolved;
    } else if (handle === 'video') {
      const resolved = getVideoUrlFromSourceResult(sourceResult);
      if (resolved) videoUrl = resolved;
    } else if (handle === 'audio') {
      const resolved = getAudioUrlFromSourceResult(sourceResult);
      if (resolved) audioUrl = resolved;
    } else if (handle === 'text') {
      if (typeof sourceResult === 'string') {
        inputText = sourceResult;
      }
    }
  }

  // Prefer typed text input from config when present (allows @mentions),
  // and fall back to text coming through the connected text handle.
  const text = (config.text || inputText || '').trim();
  const voiceID = config.voiceID || '';
  const avatarModel: 'premium' | 'standard' =
    config.avatarModel === 'standard' ? 'standard' : 'premium';
  const resolution: '480p' | '720p' =
    config.resolution === '720p' ? '720p' : '480p';
  const generateCaptions = Boolean(config.generateCaptions);
  const captionColor =
    typeof config.captionColor === 'string' ? config.captionColor : undefined;

  if (!imageUrl && !videoUrl) {
    throw new Error(
      'Talking Video node requires an image or video input. Connect an image to the purple "Image" handle or a video to the cyan "Video" handle.',
    );
  }

  if (imageUrl && videoUrl) {
    throw new Error(
      'Talking Video node can only use one of image or video at a time. Disconnect either the "Image" or "Video" input.',
    );
  }

  if (!audioUrl) {
    if (!text && !voiceID) {
      throw new Error(
        'Talking Video node requires either an audio input, or text plus a selected voice.',
      );
    }
    if (!text) {
      throw new Error(
        'Talking Video node has no text to speak. Type a script in the node settings or connect a text node to the blue "Text" handle.',
      );
    }
    if (!voiceID) {
      throw new Error(
        'Talking Video node has no voice selected. Pick a voice in the node settings.',
      );
    }
  }

  // Resolve @NodeName references in text (mirrors text-to-speech behaviour)
  const resolvedText = text
    ? resolveVariables(
        text,
        useWorkflowStore.getState().nodeResults,
        useWorkflowStore.getState().nodes,
      )
    : '';

  const payload: Record<string, unknown> = {
    avatarModel,
    resolution,
    generateCaptions,
  };

  if (imageUrl) payload.image = imageUrl;
  if (videoUrl) payload.video = videoUrl;
  if (audioUrl) {
    payload.audio = audioUrl;
  } else {
    payload.text = resolvedText;
    payload.voiceID = voiceID;
  }
  if (generateCaptions && captionColor) {
    payload.captionColor = captionColor;
  }

  const response = await fetch('/api/generate-talking-video', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(payload),
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw new Error(error.error || 'Talking video generation failed');
  }

  const result = await response.json();

  // The API returns the inserted images record. image_url is empty until the
  // webhook (WaveSpeed or VEED Fabric) writes the final URL back.
  if (result && typeof result.id === 'number') {
    if (result.image_url && result.image_url !== '') {
      return result.image_url;
    }
    return await pollForTalkingVideo(result.id);
  }

  // Fallback: try common URL fields
  const fallbackUrl =
    result?.image_url || result?.videoUrl || result?.video_url || result?.url;
  if (typeof fallbackUrl === 'string' && fallbackUrl) {
    return fallbackUrl;
  }

  throw new Error('Unexpected response from talking video generation API');
}
